import * as Yup from 'yup';

import { ResultViewModel } from './common/ResultViewModel';
import { TableViewModel } from './common/TableViewModel';
import { CustomerViewModel } from './Customer';
import { InvoiceDetailViewModel } from './InvoiceDetail';

export enum InvoiceStatus {
  Draft = 1,
  Post = 2,
  EmailSent = 3,
  Posted = 4,
  Voided = 5,
}

export const invoiceValidation = Yup.object().shape({
  customerID: Yup.number()
    .required('Customer field is Required')
    .min(1, 'Customer field is Required'),
  contactID: Yup.number().required('Contact field is Required'),
  shippingAddressID: Yup.number().required(
    'Shipping Address field is Required'
  ),
  billingAddressID: Yup.number().required('Billing Address field is Required'),
  invoiceNumber: Yup.string()
    .max(20, 'Invoice Number exceeds the maximum length of 20 characters')
    .nullable(),
  poNumber: Yup.string().max(
    20,
    'PO Number exceeds the maximum length of 20 characters'
  ),
  notes: Yup.string()
    .max(200, 'Notes exceeds the maximum length of 200 characters')
    .nullable(),
  discountType: Yup.number().required('Discount Type field is required'),
  discount: Yup.number()
    .min(0, 'Discount must be non-negative')
    .when('discountType', {
      is: 2, // Percentage
      then: (schema) =>
        schema.max(100, 'Percentage discount cannot exceed 100%'),
      otherwise: (schema) => schema,
    }),
  date: Yup.date().required('Date field is required'),
  dueDate: Yup.date()
    .when(
      'date',
      (date, Yup) => date && Yup.min(date, 'Due date must be after date')
    )
    .required('Due Date field is required'),
});

export interface InvoiceViewModel {
  id: number;
  invoiceNumber: string;
  poNumber: string;
  date: string;
  dueDate: string;
  notes: string;
  discountType?: number; // 1 for Amount, 2 for Percentage
  discount: number;
  status: number;
  customer?: CustomerViewModel;
  customerID?: number;
  contactID: number;
  contactEmail: string;
  contactPhone: string;
  isSameShipping: boolean;
  shippingAddressID: number;
  billingAddressID: number;
  isActive: true;
  isTaxExempt: boolean;
  saveMode: number;
  invoiceDetails: InvoiceDetailViewModel[];
}

export interface InvoiceVwViewModel {
  id: number;
  invoiceNumber: string;
  status: number;
  date: string;
  statusDisplayString?: string;
  customerID: number;
  customerCompanyName: string;
  subTotal?: number;
  taxAmount?: number;
  total?: number;
  totalPayment?: number;
  balance?: number;
}

export interface InvoiceTableResultViewModel
  extends TableViewModel,
    ResultViewModel {
  data: InvoiceVwViewModel[];
}

export interface InvoiceResultViewModel extends ResultViewModel {
  data: InvoiceViewModel;
}

// Invoice Default Values
export const invoiceDefaultValue: InvoiceViewModel = {
  id: 0,
  invoiceNumber: '',
  poNumber: '',
  date: '',
  dueDate: '',
  notes: '',
  discountType: 1, // Default to Amount
  discount: 0,
  status: 1,
  customer: undefined,
  customerID: 0,
  contactID: 0,
  contactEmail: '',
  contactPhone: '',
  isSameShipping: false,
  shippingAddressID: 0,
  billingAddressID: 0,
  isActive: true,
  isTaxExempt: false,
  saveMode: 1,
  invoiceDetails: [],
};

export const invoiceTableDefaultValue: InvoiceTableResultViewModel = {
  data: [],
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
  isSuccess: true,
  errorMessages: [],
};

export const getStatusText = (statusValue: number): any => {
  const statusOption = invoiceStatusSelectOptions.find(
    (option) => option.value === statusValue.toString()
  );
  return statusOption ? statusOption.text : '';
};

export const invoiceStatusSelectOptions = [
  {
    value: InvoiceStatus.Draft.toString(),
    text: 'Draft',
  },
  {
    value: InvoiceStatus.Post.toString(),
    text: 'Post',
  },
  {
    value: InvoiceStatus.EmailSent.toString(),
    text: 'Email Sent',
  },
  {
    value: InvoiceStatus.Posted.toString(),
    text: 'Posted',
  },
  {
    value: InvoiceStatus.Voided.toString(),
    text: 'Voided',
  },
];
